import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  ChangeEvent,
} from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Input, Select, Layout } from '../../../components';
import SelectType from '../../../components/Form/Select/select_type';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { FaqDataDTO } from '../../../shared/interfaces/faq';
import type { ParamsProps } from '../../../shared/interfaces/parameter';
import type { ScreenData } from '../../../shared/interfaces/screen';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Container, FormContainer, FormFooter } from './styles';

const Editar: React.FC = () => {
  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<ParamsProps>();
  const [isLoadingInput, setIsLoadingInput] = useState<boolean>(true);
  const { addToast } = useToast();
  const [dataTable, setDataTable] = useState<SelectType[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<FaqDataDTO>({
    id: ' ',
    pergunta: '',
    resposta: '',
    idTelas: [],
  });
  let selectedScreens = useRef<number[] | null>(null);

  useEffect(() => {
    if (id !== undefined) {
      findFaq(id);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get<ScreenData[]>(`/tela`);
      setIsLoadingInput(false);
      let screenValues: SelectType[] = response.data.map(
        (screen: ScreenData) => {
          return {
            value: screen.id,
            label: screen.titulo,
          };
        },
      );

      setDataTable(screenValues);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function back() {
    if (location.pathname.match('/listar')) {
      history.push('/');
    } else {
      history.goBack();
    }
  }

  function updateValue(e: ChangeEvent<HTMLInputElement>) {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  }

  const handleSubmit = useCallback(
    async (faq: FaqDataDTO) => {
      setIsLoading(true);
      faq.idTelas = selectedScreens.current;

      try {
        if (!faq.idTelas?.length) {
          const schemaSelect = Yup.object().shape({
            pergunta: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            resposta: Yup.string()
              .max(400, 'No máximo 400 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect.validate(faq, {
            abortEarly: false,
          });
        }
        const schema = Yup.object().shape({
          id: Yup.string().max(25, 'No máximo 25 caracteres'),
          pergunta: Yup.string()
            .max(200, 'No máximo 200 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
          resposta: Yup.string()
            .max(400, 'No máximo 400 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
        });

        await schema.validate(faq, {
          abortEarly: false,
        });

        if (id !== undefined) {
          await api.put(`/faq/${id}`, faq);
          setIsLoading(false);
          addToast({
            type: 'success',
            title: 'Pergunta alterada com sucesso!',
            description: 'As informações da pergunta foram alteradas.',
          });
        } else {
          await api.post('/faq', faq);
        }

        setTimeout(() => {
          history.push(RoutesPaths.listFAQ);
        }, 4000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          return;
        } else {
          console.error(err);
          addToast({
            type: 'error',
            title: 'Pergunta já cadastrada!',
            description:
              'As informações que foram inseridas, já estão presentes no sistema.',
          });
        }
      }
    },
    [addToast, history, id],
  );

  async function findFaq(id: string) {
    const response = await api.get<FaqDataDTO>(`/faq/${id}`);
    setData({
      id: id,
      pergunta: response.data.pergunta,
      resposta: response.data.resposta,
      idTelas: response.data.idTelas,
    });

    selectedScreens.current = response.data.idTelas;
  }

  function handleScreenChange(dataTable: any | null) {
    let screens: number[] = [];
    if (dataTable) {
      screens = dataTable.map((item: any) => item['value']);
    }

    const newData = data;
    newData.idTelas = screens;

    selectedScreens.current = screens;
    setData(newData);
  }

  return (
    <>
      <Layout title={'Editar Pergunta'} hideFloatingButton>
        <Container>
          <FormContainer>
            <Form onSubmit={handleSubmit} ref={formRef}>
              <Input
                type="text"
                label="Pergunta"
                name="pergunta"
                value={data.pergunta}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
                loading={isLoadingInput}
              ></Input>

              <Input
                type="text"
                label="Resposta"
                name="resposta"
                value={data.resposta}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
                loading={isLoadingInput}
              ></Input>

              <Select
                name={'select'}
                options={dataTable}
                onChange={handleScreenChange}
                placeholder="Selecione"
                initialValues={data.idTelas}
                label="Telas"
                loading={!dataTable.length}
              />

              <FormFooter>
                <Button
                  variant="solid"
                  type="submit"
                  style={{ marginTop: '1rem' }}
                >
                  Salvar
                </Button>
                <Button
                  variant="outline"
                  color={'#C71F37'}
                  style={{ marginTop: '1rem' }}
                  onClick={() =>
                    setTimeout(() => {
                      back();
                    }, 300)
                  }
                >
                  Cancelar
                </Button>
              </FormFooter>
            </Form>
          </FormContainer>
        </Container>
      </Layout>
    </>
  );
};

export default Editar;
